import React, { useState, useEffect, useMemo, forwardRef } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useOrgan } from "../../hooks/useOrgan";
import { organizationPrimaryColors } from "../../layouts/PermanentSidebarMUI";
import { maskCurrency, maskInteger } from "../../utils/maskUtils";
import { TableVirtuoso } from "react-virtuoso";

const ItemListSection = ({
  section,
  description,
  fields,
  fieldsState,
  initialData = [],
  onItemsChange,
  renderFormBody,
  getItemDisplayValue,
  onFormSubmit,
  itemFormData,
  onItemChange,
  itemFormErrors,
  initializeItemForm,
  isDisabledButton = false,
  getOptions,
  getSelectedValue,
  getSelectMeta,
  isEditMode = false,
  disabledFields = [],
  headerCustom = () => {},
  setIsEditingMode = () => {},
  disableActions = [],
  notShowFields = [],
  hideColumnAciton = false,
  notShowHeader = [],
  useVirtualization = false,
  tableHeight = 500,
  captureAction = () => {},
  // Propriedade usada em casos de excesão, para exclusão de itens relacionados
  onDeleteItem = () => {},
  showAddButton = true,
}) => {
  const [items, setItems] = useState(
    Array.isArray(initialData) ? initialData : [],
  );
  const [openDialog, setOpenDialog] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setItems(Array.isArray(initialData) ? initialData : []);
  }, [initialData]);

  // Formulário usa fieldsState (dinâmico) se disponível, senão usa fields
  const formFields = fieldsState || fields;

  // Lógica para verificar se o formulário está preenchido
  const isItemFormFilled = useMemo(() => {
    if (!itemFormData || typeof itemFormData !== "object") return false;

    const dynamicKey = Object.keys(itemFormData)[0];
    if (!dynamicKey) return false;

    const formValues = itemFormData[dynamicKey];
    if (!formValues || typeof formValues !== "object") return false;

    const requiredFields = formFields.filter((field) => !field.notRequired);

    const isValidValue = (value) => {
      // null ou undefined → inválido
      if (value === null || value === undefined) return false;

      // string vazia ou só espaços → inválido
      if (typeof value === "string" && value.trim() === "") return false;

      // array vazio → inválido
      if (Array.isArray(value) && value.length === 0) return false;

      // number (incluindo 0) → válido
      if (typeof value === "number") return true;

      // boolean → válido
      if (typeof value === "boolean") return true;

      // objeto
      if (typeof value === "object") {
        // se tiver propriedade value (select pattern)
        if ("value" in value) {
          return isValidValue(value.value);
        }

        // objeto vazio → inválido
        return Object.keys(value).length > 0;
      }

      return true;
    };

    return requiredFields.every((field) => {
      const fieldKey = field.id.split(".").pop();
      const fieldValue = formValues[fieldKey];

      return isValidValue(fieldValue);
    });
  }, [itemFormData, formFields]);

  // Se for modo de edição não permitir editar items
  useEffect(() => {
    // Se for edição e o formulário estiver preenchido
    if (isEditMode) {
      if (isItemFormFilled) {
        // Desabilita os campos
        fields.forEach((field) => {
          field.disabled = disabledFields.includes(field.id);
        });
      } else {
        // Habilita os campos
        fields.forEach((field) => {
          field.disabled = false;
        });
      }
    }
  }, [isEditMode, disabledFields, isItemFormFilled, fields]);

  const handleOpenDialog = (item = null) => {
    if (item) {
      setItemToEdit(item);
      setIsEditing(true);
      setIsEditingMode(true);
      initializeItemForm(item);
    } else {
      setItemToEdit(null);
      setIsEditing(false);
      setIsEditingMode(false);
      initializeItemForm(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setItemToEdit(null);
    initializeItemForm(null);
    setIsEditingMode(null);
  };

  const handleSaveItem = () => {
    const success = onFormSubmit(itemToEdit);
    if (success) {
      handleCloseDialog(); // só fecha se salvou de verdade
    }
  };

  const handleDeleteItem = (itemToDelete) => {
    const safeItems = Array.isArray(items) ? items : [];
    const updatedItems = safeItems.filter(
      (item) => item.id !== itemToDelete.id,
    );
    setItems(updatedItems);
    onItemsChange(updatedItems);
    if (onDeleteItem) {
      onDeleteItem(itemToDelete);
    }
  };

  const { selectedOrgan } = useOrgan();
  const primaryColor = selectedOrgan?.name
    ? organizationPrimaryColors[selectedOrgan.name.toUpperCase()] ||
      organizationPrimaryColors.ALUCOM
    : organizationPrimaryColors.ALUCOM;

  const formatters = {
    integer: (value) =>
      value !== null && value !== undefined ? maskInteger(value) : "",
    currency: (value) => {
      if (value === null || value === undefined) return "";
      if (typeof value === "string" && value.includes("R$")) return value;
      return maskCurrency(value);
    },

    date: (value) => {
      if (!value) return "";

      if (typeof value === "string" && value.includes("/")) return value;

      try {
        const [year, month, day] = value.split("-");
        if (year && month && day) {
          return `${day}/${month}/${year}`;
        }
        return new Date(value).toLocaleDateString("pt-BR", { timeZone: "UTC" });
      } catch (e) {
        return value;
      }
    },
    text: (value) => value || "",
  };

  const formatValue = (field, value) => {
    const formatType = field.formatType || "text";
    const formatter = formatters[formatType] || formatters.text;
    return formatter(value);
  };

  const fieldsCustom =
    notShowFields.length > 0
      ? fields.filter((field) => !notShowFields.includes(field.id))
      : fields;

  const visibleHeaders = fieldsCustom.filter(
    (field) => !notShowHeader.includes(field.id),
  );

  // Mapeamento de componentes para manter o estilo do MUI
  const VirtuosoTableComponents = useMemo(
    () => ({
      Scroller: forwardRef((props, ref) => (
        <TableContainer component={Paper} {...props} ref={ref} />
      )),
      Table: (props) => (
        <Table
          {...props}
          sx={{ borderCollapse: "separate", tableLayout: "fixed" }}
        />
      ),
      TableHead: forwardRef((props, ref) => (
        <TableHead
          {...props}
          ref={ref}
          sx={{ backgroundColor: primaryColor, zIndex: 2 }}
        />
      )),
      TableRow: (props) => <TableRow {...props} />,
      TableBody: forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
    }),
    [primaryColor],
  );

  return (
    <Box sx={{ mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mb: 2,
        }}>
        {/* Seção de Título e Descrição */}
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 0.5,
              textTransform: "uppercase",
              fontSize: 18,
            }}
            className="section-border">
            {section}
          </Typography>
          {description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, fontSize: 14 }}>
              {description}
            </Typography>
          )}
        </Box>

        {/* Botão de Adicionar */}
        {showAddButton && (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            handleOpenDialog();
            captureAction("add");
          }}
          disabled={isDisabledButton}
          sx={{
            backgroundColor: primaryColor,
            color: "#fff",
            whiteSpace: "nowrap",
            minWidth: "fit-content",
            fontWeight: "bold",
            "&:hover": {
              backgroundColor: primaryColor,
              filter: "brightness(0.9)",
            },
          }}>
          Adicionar Item
        </Button>
        )}
      </Box>

      {/* Container com altura condicional */}
      <Box
        sx={{
          height: useVirtualization ? tableHeight : "auto",
          width: "100%",
          mt: 2,
        }}>
        {items?.length > 0 ? (
          useVirtualization ? (
            <TableVirtuoso
              data={items}
              components={VirtuosoTableComponents}
              fixedHeaderContent={() => (
                <TableRow sx={{ backgroundColor: primaryColor }}>
                  {/* Usa visibleHeaders ao invés de fieldsCustom */}
                  {visibleHeaders.map((field) => (
                    <TableCell
                      key={field.id}
                      sx={{
                        color: "#FFFFFF",
                        fontWeight: "bold",
                        backgroundColor: primaryColor,
                        fontSize: "0.875rem",
                      }}>
                      {field.label}
                    </TableCell>
                  ))}
                  {!hideColumnAciton && (
                    <TableCell
                      align="right"
                      sx={{
                        color: "#FFFFFF",
                        fontWeight: "bold",
                        backgroundColor: primaryColor,
                        fontSize: "0.875rem",
                        width: 120,
                      }}>
                      Ações
                    </TableCell>
                  )}
                </TableRow>
              )}
              itemContent={(index, item) => (
                <>
                  {/* Renderiza TODAS as células (fieldsCustom) mas só mostra headers dos visibleHeaders */}
                  {fieldsCustom.map((field) => {
                    // Se o header está oculto, não renderiza a célula
                    if (notShowHeader.includes(field.id)) return null;

                    return (
                      <Tooltip
                        title={getItemDisplayValue(item, field.id)}
                        key={field.id}
                        placement="bottom-start">
                        <TableCell>
                          {formatValue(
                            field,
                            getItemDisplayValue(item, field.id),
                          )}
                        </TableCell>
                      </Tooltip>
                    );
                  })}
                  {!hideColumnAciton && (
                    <TableCell align="right">
                      {!disableActions.includes("edit") && (
                        <IconButton
                          onClick={() => {
                            handleOpenDialog(item);
                            captureAction("edit");
                          }}
                          size="small">
                          <EditIcon />
                        </IconButton>
                      )}
                      {!disableActions.includes("delete") && (
                        <IconButton
                          onClick={() => {
                            handleDeleteItem(item);
                            captureAction("delete");
                          }}
                          size="small">
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </>
              )}
            />
          ) : (
            /* Versão SEM virtualização - Mesma lógica */
            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ backgroundColor: primaryColor }}>
                  <TableRow>
                    {/* Usa visibleHeaders */}
                    {visibleHeaders.map((field) => (
                      <TableCell
                        key={field.id}
                        sx={{
                          color: "#FFFFFF",
                          fontWeight: "bold",
                          fontSize: "0.875rem",
                        }}>
                        {field.label}
                      </TableCell>
                    ))}
                    {!hideColumnAciton && (
                      <TableCell
                        align="right"
                        sx={{
                          color: "#FFFFFF",
                          fontWeight: "bold",
                          width: 120,
                          fontSize: "0.875rem",
                        }}>
                        Ações
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={item.id || index}>
                      {/* Renderiza só células visíveis */}
                      {fieldsCustom.map((field) => {
                        if (notShowHeader.includes(field.id)) return null;

                        return (
                          <Tooltip
                            title={getItemDisplayValue(item, field.id)}
                            key={field.id}
                            placement="bottom-start">
                            <TableCell sx={{ textTransform: "uppercase" }}>
                              {formatValue(
                                field,
                                getItemDisplayValue(item, field.id),
                              )}
                            </TableCell>
                          </Tooltip>
                        );
                      })}
                      {!hideColumnAciton && (
                        <TableCell align="right">
                          {!disableActions.includes("edit") && (
                            <IconButton
                              onClick={() => {
                                handleOpenDialog(item);
                                captureAction("edit");
                              }}
                              size="small">
                              <EditIcon />
                            </IconButton>
                          )}
                          {!disableActions.includes("delete") && (
                            <IconButton
                              onClick={() => {
                                handleDeleteItem(item);
                                captureAction("delete");
                              }}
                              size="small">
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : (
          /* Tabela vazia - Usa visibleHeaders */
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: primaryColor }}>
                <TableRow>
                  {visibleHeaders.map((field) => (
                    <TableCell
                      key={field.id}
                      sx={{
                        color: "#FFFFFF",
                        fontWeight: "bold",
                        fontSize: "0.875rem",
                      }}>
                      {field.label}
                    </TableCell>
                  ))}
                  {!hideColumnAciton && (
                    <TableCell
                      align="right"
                      sx={{
                        color: "#FFFFFF",
                        fontWeight: "bold",
                        width: 120,
                        fontSize: "0.875rem",
                      }}>
                      Ações
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={visibleHeaders.length + 1} align="center">
                    Nenhum item adicionado.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="lg">
        <DialogTitle
          sx={{
            backgroundColor: primaryColor,
            color: "#FFFFFF",
            fontWeight: "bold",
          }}>
          {isEditing ? "Editar Item" : "Adicionar Item"}
        </DialogTitle>
        <DialogContent>
          {/* Header customizado */}
          {headerCustom()}
          <Box sx={{ mt: 2 }}>
            {renderFormBody({
              formData: itemFormData,
              handleFieldChange: onItemChange,
              fields: formFields,
              formErrors: itemFormErrors,
              getOptions,
              getSelectedValue,
              getSelectMeta,
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            sx={{ backgroundColor: "#FFFFFF", color: primaryColor }}>
            Cancelar
          </Button>
          <Button
            onClick={handleSaveItem}
            disabled={!isItemFormFilled}
            variant="contained"
            color="primary"
            sx={{ backgroundColor: primaryColor, color: "#FFFFFF" }}>
            {isEditing ? "Salvar" : "Adicionar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ItemListSection;
